import { useEffect, useState } from "react";
import { useAppSelector } from "./redux";
import type { IAnalyticsController } from "../hexagon/adapters/driver/analyticsController";
import { isMobile } from "../util/layout";
import {
  shouldExcludePath,
  markManuallyOpenedThisSession,
  type IntercomTriggerOptions,
} from "../util/intercomTrigger";
import { isHomeHealthExternalUser } from "../constants/sso";

interface IUseIntercom {
  webIntercomProvider: IWebIntercomProvider;
  analyticsController: IAnalyticsController;
}

export const useIntercom = ({
  webIntercomProvider,
  analyticsController,
}: IUseIntercom) => {
  const [toggle, setToggle] = useState(false);

  const { user, userClinic } = useAppSelector((state) => ({
    user: state.auth.user,
    userClinic: state.clinic.userClinic,
  }));

  useEffect(() => {
    // Don't boot Intercom for HomeHealth External users or Compassus subdomain
    if (isHomeHealthExternalUser(user?.groups)) return;

    webIntercomProvider.boot({
      hide_default_launcher: false,
      name: user?.fullName || undefined,
      facility: userClinic?.name || undefined,
      email: user?.email || undefined,
      user_id: user?.userID || undefined,
    });

    // Global event listener for intercom triggers
    const handleIntercomEvent = (event: Event) => {
      // Don't trigger on mobile
      if (isMobile()) return;

      const customEvent = event as CustomEvent<IntercomTriggerOptions>;
      const options = customEvent.detail || {};
      const { delay = 0, excludePaths = [] } = options;

      // Check if current path should be excluded
      if (shouldExcludePath(excludePaths)) {
        return;
      }

      if (delay > 0) {
        setTimeout(() => {
          handleToggle();
        }, delay);
      } else {
        handleToggle();
      }
    };

    // Add global event listener
    window.addEventListener("openIntercom", handleIntercomEvent);

    return () => {
      webIntercomProvider.shutdown();
      window.removeEventListener("openIntercom", handleIntercomEvent);
    };
  }, []);

  const tapEvent = () => {
    analyticsController.logEvent("Tap on intercom icon", {
      email: user?.email,
      selectedFacility: userClinic?.name,
      selectedFacilityId: userClinic?.id,
      cUserId: user?.userID,
    });
  };

  const handleWebToggle = () => {
    if (toggle === true) {
      webIntercomProvider.hide();
      setToggle(false);
    } else {
      tapEvent();
      setToggle(true);

      // Mark that user manually opened Intercom this session
      // This prevents auto-trigger for the remainder of this session
      markManuallyOpenedThisSession();

      webIntercomProvider.show();
    }
  };

  // const handleNativeToggle = async () => {
  //   if (toggle === true) {
  //     await circadiaEngineProvider.hideIntercom();
  //     setToggle(false);
  //   } else {
  //     await circadiaEngineProvider.showIntercom();
  //     tapEvent();
  //     setToggle(true);
  //   }
  // };

  const handleToggle = async () => {
    // Temporary disable native intercom
    // if (false) {
    //   handleNativeToggle();
    //   return;
    // }
    handleWebToggle();
  };

  webIntercomProvider.onHide(() => {
    setToggle(false);
  });

  webIntercomProvider.onShow(() => {
    setToggle(true);
  });

  return { handleToggle };
};
